import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon
  const [companies, setCompanies] = useState(null); // null = not yet loaded
  const [company, setCompany] = useState(null);
  const [theme, setTheme] = useState(localStorage.getItem("fx_theme") || "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("fx_theme", theme);
  }, [theme]);

  const loadCompanies = useCallback(async () => {
    const { data } = await api.get("/companies");
    setCompanies(data);
    const saved = localStorage.getItem("fx_company");
    const picked = data.find((c) => c.id === saved) || data[0] || null;
    if (picked) localStorage.setItem("fx_company", picked.id);
    else localStorage.removeItem("fx_company");
    setCompany(picked);
    return data;
  }, []);

  const bootstrap = useCallback(async () => {
    if (!localStorage.getItem("fx_token")) {
      setUser(false);
      setCompanies([]);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadCompanies();
    } catch {
      localStorage.removeItem("fx_token");
      setUser(false);
      setCompanies([]);
    }
  }, [loadCompanies]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("fx_token", data.access_token);
    setUser(data.user);
    await loadCompanies();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("fx_token", data.access_token);
    setUser(data.user);
    setCompanies([]);
    setCompany(null);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("logout request failed, clearing local session anyway", e);
    }
    localStorage.removeItem("fx_token");
    localStorage.removeItem("fx_company");
    setUser(false);
    setCompany(null);
    setCompanies([]);
  };

  const switchCompany = (c) => {
    localStorage.setItem("fx_company", c.id);
    setCompany(c);
    window.location.reload();
  };

  return (
    <AppContext.Provider
      value={{
        user, companies, company, theme, setTheme,
        login, register, logout, switchCompany, loadCompanies, apiError,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}
